import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useObjectRecordsByType} from '@/matic/features/master-data/hooks/useObjectRecordsByType';
import {useObjectTypes} from '@/matic/features/master-data/hooks/useObjectTypes';
import type {ObjectType} from '@/matic/features/master-data/types/master-data-types';
import type {OttoSessionId} from '@/matic/features/agents/otto/types/otto-models';
import {usePins} from './usePins';
import {useRequiredAssociationTypes} from './useRequiredAssociationTypes';
import {useRecentlyAccessedObjects} from './useRecentlyAccessedObjects';
import {useChatDraftStore} from '../stores/chat-draft-store';
import {
  parseScopeParam,
  encodeScopeParam,
  scopeKeyOf,
  type ScopeItem,
} from '../lib/scope-utils';

/** The draft sentinel: `chat=new` means a blank, un-minted conversation. */
export const DRAFT_SENTINEL = 'new';

/** `?browse=tags:<typeId>` — the Category roster browse view. */
export const TAGS_BROWSE_PREFIX = 'tags:';

/** Build the `browse` param value for a Category's roster. */
export function tagsBrowseParam(typeId: string) {
  return `${TAGS_BROWSE_PREFIX}${typeId}`;
}

export const COMPOSER_ATTENTION_PARAM = 'composer';
export const COMPOSER_ATTENTION_VALUE = 'focus';

/**
 * The shared projection powering both the consolidated Library rail
 * (`LibraryRail`, hosted in `SidebarNav`) and the Library canvas
 * (`LibraryContainer`). Because the two are sibling React subtrees that can't
 * share component state, everything here is derived from global sources — the
 * URL search params, the react-query cache, and localStorage — so a value set
 * by one subtree is observed by the other with no prop wiring. react-query
 * dedupes the shared queries by key, so consuming this hook from both places
 * does not double-fetch.
 *
 * The handlers exposed here are **pure URL writers** (plus the localStorage
 * recency bump where the original did). They intentionally carry none of the
 * canvas-local side effects the container layers on top (active tab, thread
 * highlight, mint machinery) — those are reproduced by URL-driven effects in
 * the container, so the same handler drives the canvas identically whether it's
 * called from the rail or from within the canvas.
 */
export function useLibraryRail() {
  const [searchParams, setSearchParams] = useSearchParams();
  const removeOptimisticRunningId = useChatDraftStore(
    (s) => s.removeOptimisticRunningId
  );

  // -- URL reads ---------------------------------------------------------
  const scopeParam = searchParams.get('scope') ?? '';
  const scopeItems = useMemo(
    () => (scopeParam ? parseScopeParam(scopeParam) : []),
    [scopeParam]
  );
  const scopeKey = useMemo(() => scopeKeyOf(scopeItems), [scopeItems]);
  const browseParam = searchParams.get('browse') ?? undefined;
  /** The Category roster ("See all Customers →" from the breadcrumb). Same
   *  `browse=` family as the tenant-wide Files browser, so it is a browse view
   *  rather than a tab — the roster is the level *below* the Category, which
   *  the breadcrumb navigates to, not a lens on it. */
  const browsedTagsTypeId = browseParam?.startsWith(TAGS_BROWSE_PREFIX)
    ? browseParam.slice(TAGS_BROWSE_PREFIX.length)
    : undefined;
  const isBrowsingFiles = browseParam === '__files__';
  const browsedCategoryId = isBrowsingFiles
    ? (searchParams.get('category') ?? undefined)
    : undefined;
  const selectedThreadId = searchParams.get('thread') ?? undefined;
  const selectedMessageId = searchParams.get('msg') ?? undefined;
  const chatParam = searchParams.get('chat') ?? undefined;
  const isDraft = chatParam === DRAFT_SENTINEL;
  const selectedSessionId = isDraft ? undefined : chatParam;

  // Atomic URL-param update with the Library's standard `{replace: true}`.
  const updateParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  // -- Browse data -------------------------------------------------------
  const typesQuery = useObjectTypes({limit: 100});
  const types = useMemo<ObjectType[]>(
    () => typesQuery.data?.items ?? [],
    [typesQuery.data]
  );

  const {data: requiredTypesData} = useRequiredAssociationTypes();
  const requiredTypeIds = useMemo(
    () => (requiredTypesData ?? []).map((t) => t.objectTypeId),
    [requiredTypesData]
  );

  const typeIds = useMemo(() => types.map((t) => t.id), [types]);
  const {recordsByType, isLoading: recordsLoading} =
    useObjectRecordsByType(typeIds);

  const {recentlyAccessed, recordAccess} = useRecentlyAccessedObjects();
  const {data: pinsData} = usePins();
  const pins = useMemo(() => pinsData ?? [], [pinsData]);

  // -- Handlers (pure URL writers, + recency where the original did) -----

  // Set scope to a single record (replaces any existing scope) — the "open
  // this record's page" moment, so it also bumps the recency list.
  const handleSelectRecord = useCallback(
    (typeId: string, recordId: string) => {
      updateParams((next) => {
        next.set('scope', `${typeId}:${recordId}`);
        next.delete('browse');
        next.delete('thread');
        next.delete('msg');
        next.delete('chat');
        // Opening a record fresh must not carry a type page's Views selectors
        // (a type-scope `template`/`tab=views`) into the record scope — the
        // stream would land on a bogus type Definition instead of Activity.
        next.delete('tab');
        next.delete('template');
        next.delete('view');
      });
      recordAccess(typeId, recordId);
    },
    [updateParams, recordAccess]
  );

  // Toggle a record in/out of scope (for "Also tagged" additions).
  const handleToggleScopeRecord = useCallback(
    (typeId: string, recordId: string) => {
      let adding = false;
      updateParams((next) => {
        const existing = parseScopeParam(next.get('scope') ?? '');
        const alreadyIn = existing.some(
          (item) => item.typeId === typeId && item.recordId === recordId
        );
        adding = !alreadyIn;
        let updated: ScopeItem[];
        if (alreadyIn) {
          updated = existing.filter(
            (item) => !(item.typeId === typeId && item.recordId === recordId)
          );
        } else {
          updated = [...existing, {typeId, recordId}];
        }

        if (updated.length === 0) {
          next.delete('scope');
          next.delete('thread');
          next.delete('msg');
        } else {
          next.set('scope', encodeScopeParam(updated));
        }
      });
      if (adding) recordAccess(typeId, recordId);
    },
    [updateParams, recordAccess]
  );

  // Clear scope entirely — the Browse "Home" row's action. This is primary
  // navigation back to the neutral Library home, so it also drops any open
  // chat/draft state instead of preserving the chat tab.
  const handleClearScope = useCallback(() => {
    updateParams((next) => {
      next.delete('scope');
      next.delete('thread');
      next.delete('msg');
      next.delete('browse');
      next.delete('category');
      next.delete('chat');
      next.delete('tab');
      next.delete(COMPOSER_ATTENTION_PARAM);
      // Clearing scope returns to the unscoped landing — drop the durable
      // `view=activity` escape hatch so Home lands on Home, not the feed.
      next.delete('view');
    });
  }, [updateParams]);

  // Open a Category as the canonical wildcard scope. The UI calls object types
  // Categories and their records Tags; the backend grammar remains `type:*`.
  const handleSelectType = useCallback(
    (typeId: string, tab?: 'views' | 'records') => {
      updateParams((next) => {
        next.set('scope', `${typeId}:*`);
        next.set('tab', tab === 'records' ? 'tags' : 'views');
        next.delete('browse');

        next.delete('template');
        next.delete('view');
        next.delete('thread');
        next.delete('msg');
        next.delete('chat');
      });
    },
    [updateParams]
  );

  // Open a conversation: pin it to the URL, restore its scope. Purely
  // client-side and synchronous — no archive/restore.
  const handleSelectSession = useCallback(
    (sessionId: OttoSessionId, items: ScopeItem[]) => {
      updateParams((next) => {
        const prevScopeKey = scopeKeyOf(
          parseScopeParam(next.get('scope') ?? '')
        );
        const nextScopeKey = scopeKeyOf(items);

        next.set('chat', sessionId);
        if (items.length > 0) {
          next.set('scope', encodeScopeParam(items));
        } else {
          next.delete('scope');
        }

        next.delete('browse');
        // The RHS inspector is an independent pane from the Otto dock, so
        // landing on a conversation must not close the thread the user is
        // reading — this is also the mint-on-first-send path (which passes the
        // *current* scope), where dropping it made the base composer silently
        // dismiss the open thread detail. A genuine scope change does
        // invalidate the selection: that thread belongs to the scope you left.
        if (prevScopeKey !== nextScopeKey) {
          next.delete('thread');
          next.delete('msg');
        }
      });
    },
    [updateParams]
  );

  // A conversation was hard-deleted. Drop its own optimistic spinner bridge
  // entry (only its own — other sessions may still be mid-mint) and, if it was
  // the open one, the dangling `chat` reference.
  const handleSessionDeleted = useCallback(
    (sessionId: OttoSessionId) => {
      removeOptimisticRunningId(sessionId);
      if (sessionId !== selectedSessionId) return;
      updateParams((next) => {
        next.delete('chat');
      });
    },
    [selectedSessionId, updateParams, removeOptimisticRunningId]
  );

  // Open a blank draft conversation. No-op when already on an empty draft.
  const handleOpenDraft = useCallback(
    (options?: {focusComposer?: boolean}) => {
      if (
        searchParams.get('chat') === DRAFT_SENTINEL &&
        !options?.focusComposer
      ) {
        return;
      }

      updateParams((next) => {
        next.set('chat', DRAFT_SENTINEL);
        if (options?.focusComposer) {
          next.set(COMPOSER_ATTENTION_PARAM, COMPOSER_ATTENTION_VALUE);
        }

        next.delete('browse');
        // Scope is untouched here, so an open thread is still in-scope — and
        // opening the dock is orthogonal to what the RHS inspector is showing.
        // Leave `thread`/`msg` alone (see `handleSelectSession`).
      });
    },
    [searchParams, updateParams]
  );

  return {
    // Reads
    searchParams,
    scopeParam,
    scopeItems,
    scopeKey,
    browseParam,
    browsedTagsTypeId,
    isBrowsingFiles,
    browsedCategoryId,
    selectedThreadId,
    selectedMessageId,
    chatParam,
    isDraft,
    selectedSessionId,
    // Url helpers
    updateParams,
    // Browse data
    types,
    typeIds,
    typesLoading: typesQuery.isLoading,
    recordsByType,
    recordsLoading,
    requiredTypeIds,
    recentlyAccessed,
    recordAccess,
    pins,
    // Handlers
    handleSelectRecord,
    handleToggleScopeRecord,
    handleClearScope,
    handleSelectType,
    handleSelectSession,
    handleSessionDeleted,
    handleOpenDraft,
  };
}
